import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaClipboardList } from "react-icons/fa";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomTextInput,
  CustomDropdown,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { fetchClassOptions } from "../../../../redux/Masterdata";

const AdmissionSeatAllocationConfigCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);
  const { classOptions } = useSelector((state) => state.masterData);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const academic_year = clientData?.academic_year || "";
  const user_id = user?.id || "";

  // Edit mode detection
  const editId = location.state?.id || null;
  const isEditMode = !!editId;

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Static medium options
  const mediumOptions = [
    { label: "Tamil", value: "Tamil" },
    { label: "English", value: "English" },
    { label: "Hindi", value: "Hindi" },
  ];

  // Group dropdown options and raw data
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupRawData, setGroupRawData] = useState([]);

  // Higher class check (XI or XII)
  const [isHigherClass, setIsHigherClass] = useState(false);

  const [formData, setFormData] = useState({
    classId: "",
    medium: "",
    groupId: "",
    subjects: "",
    totalCapacity: "",
  });

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });

  // Classes that require Group & Subjects
  const HIGHER_CLASS_NAMES = ["XI", "XII", "11", "12"];

  // Check if the selected class is XI or XII
  const checkIsHigherClass = (classValue) => {
    if (!classValue || !classOptions?.length) return false;
    const selectedOption = classOptions.find(
      (opt) => opt.value === classValue || opt.value === classValue?.value,
    );
    if (!selectedOption) return false;
    const className = (selectedOption.label || "").trim().toUpperCase();
    return HIGHER_CLASS_NAMES.includes(className);
  };

  // Fetch class options via Redux on mount
  useEffect(() => {
    if (school_id) {
      dispatch(
        fetchClassOptions({
          id: parseInt(school_id),
          academic_year: academic_year,
        }),
      );
    }
  }, [dispatch, school_id, academic_year]);

  // Fetch data for edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchSeatConfigData();
    }
  }, [editId]);

  // Fetch group list when higher class is detected
  useEffect(() => {
    if (isHigherClass) {
      fetchGroupList();
    } else {
      setGroupOptions([]);
      setGroupRawData([]);
      setFormData((prev) => ({ ...prev, groupId: "", subjects: "" }));
    }
  }, [isHigherClass]);

  const fetchGroupList = async () => {
    try {
      const response = await AxiosInstance.post("/school/listGroup", {
        page: "",
        limit: "",
        search: "",
      });
      if (response?.data?.rows) {
        const rawData = response.data.rows || [];
        setGroupRawData(rawData);
        const options = rawData.map((item) => ({
          label: item.name || "",
          value: item.id || "",
        }));
        setGroupOptions(options);
      }
    } catch (error) {
      console.error("Error fetching group list:", error);
    }
  };

  const fetchSeatConfigData = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/admission/viewSeatConfig", {
        id: Number(editId),
      });

      if (response?.data?.status) {
        const data = response.data.data;

        // Determine if higher class for edit mode
        const className = (data.class_name || "").trim().toUpperCase();
        const higherClass = HIGHER_CLASS_NAMES.includes(className);
        setIsHigherClass(higherClass);

        setFormData({
          classId: data.class_id || "",
          medium: data.medium || "",
          groupId: data.group_id ? String(data.group_id) : "",
          subjects: data.subject_name || "",
          totalCapacity:
            data.group_max_seat_count != null
              ? String(data.group_max_seat_count)
              : data.section_max_seat_count != null
                ? String(data.section_max_seat_count)
                : "",
        });
        fetchTotalStudentCount(data.class_id, data.medium);
      }
    } catch (error) {
      console.error("Error fetching seat config:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations?.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalStudentCount = async (classId, medium) => {
    if (!classId || !medium) return;
    // if (isEditMode) return;

    // Convert medium to API format
    const mediumMap = {
      Tamil: "TM",
      English: "EM",
      Hindi: "HI",
    };

    const mediumCode = mediumMap[medium] || medium;

    try {
      const response = await AxiosInstance.post(
        "/admission/get-total-max-students",
        {
          school_id: Number(school_id),
          class_id: Number(classId),
          medium: mediumCode,
        },
      );

      if (response?.data?.status) {
        const total = response.data.total_max_students || "";

        setFormData((prev) => ({
          ...prev,
          totalCapacity: total,
        }));
      }
    } catch (error) {
      console.error("Error fetching total max students:", error);
    }
  };

  // Handle totalCapacity - allow only numbers, max 3 digits
  const handleCapacityChange = (e) => {
    const { value } = e.target;
    const numbersOnly = value.replace(/[^0-9]/g, "");
    if (numbersOnly.length <= 3) {
      setFormData((prev) => ({ ...prev, totalCapacity: numbersOnly }));
      if (errors.totalCapacity) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.totalCapacity;
          return newErrors;
        });
      }
    }
  };

  // Handle dropdown change
  const handleDropdownChange = (name, value) => {
    const selectedValue = value?.value || value;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Class change
    if (name === "classId") {
      const higher = checkIsHigherClass(selectedValue);
      setIsHigherClass(higher);

      const mediumValue = formData.medium?.value || formData.medium;

      if (!higher) {
        setFormData((prev) => ({
          ...prev,
          classId: value,
          groupId: "",
          subjects: "",
        }));
      }

      if (selectedValue && mediumValue) {
        fetchTotalStudentCount(selectedValue, mediumValue);
      }
    }

    // Medium change
    if (name === "medium") {
      const classValue = formData.classId?.value || formData.classId;

      if (classValue && selectedValue) {
        fetchTotalStudentCount(classValue, selectedValue);
      }
    }

    // Group change
    if (name === "groupId") {
      const selectedGroup = groupRawData.find(
        (g) => String(g.id) === String(selectedValue),
      );

      setFormData((prev) => ({
        ...prev,
        groupId: value,
        subjects: selectedGroup?.subject_names || "",
      }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.classId) {
      newErrors.classId =
        translations?.labels?.classRequired || "Class is required";
    }
    if (!formData.medium) {
      newErrors.medium =
        translations?.labels?.mediumRequired || "Medium is required";
    }
    if (isHigherClass && !formData.groupId) {
      newErrors.groupId =
        translations?.labels?.groupRequired || "Group is required";
    }
    if (!formData.totalCapacity?.toString().trim()) {
      newErrors.totalCapacity =
        translations?.labels?.totalCapacityRequired ||
        "Total Capacity is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      let response;
      const classValue = Number(formData.classId?.value || formData.classId);
      const mediumValue = formData.medium?.value || formData.medium || "";
      const groupValue = isHigherClass
        ? String(formData.groupId?.value || formData.groupId || "")
        : "";

      // Conditional capacity keys
      const groupMaxSeat = isHigherClass ? formData.totalCapacity || "" : "";
      const sectionMaxSeat = isHigherClass ? "" : formData.totalCapacity || "";

      if (isEditMode) {
        const payload = {
          id: Number(editId),
          academic_year_id: Number(academic_year_id),
          class: classValue,
          medium: mediumValue,
          group_id: groupValue,
          group_max_seat_count: groupMaxSeat,
          section_max_seat_count: sectionMaxSeat,
          section_min_seat_count: "",
          updated_by: Number(user_id),
        };

        response = await AxiosInstance.put(
          "/admission/updateSeatConfig",
          payload,
        );
      } else {
        const payload = {
          client_id: Number(client_id),
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
          class: classValue,
          medium: mediumValue,
          group_id: groupValue,
          group_max_seat_count: groupMaxSeat,
          section_max_seat_count: sectionMaxSeat,
          section_min_seat_count: "",
          created_by: Number(user_id),
        };

        response = await AxiosInstance.post(
          "/admission/createSeatConfig",
          payload,
        );
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            (isEditMode
              ? translations?.labels?.updateSuccess || "Updated successfully!"
              : translations?.labels?.createSuccess || "Created successfully!"),
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            translations?.labels?.saveFailed ||
            "Failed to save. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error saving seat config:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations?.labels?.saveFailed ||
          "Failed to save. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle modal ok
  const handleSuccessOk = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    if (modalState.type === "success") {
      navigate("/user/admission-seat-allocation-config");
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    navigate("/user/admission-seat-allocation-config");
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/user/admission-seat-allocation-config");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {translations?.labels?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations?.labels?.seatAllocationConfiguration ||
              "Seat Allocation Configuration"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Academic Year (Read-only) */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations?.labels?.academicYear || "Academic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
                required
              />
            </div>

            {/* Class Dropdown - from Redux */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations?.labels?.classLabel || "Class"}
                name="classId"
                value={formData.classId}
                onChange={(value) => handleDropdownChange("classId", value)}
                options={classOptions}
                placeholder={
                  translations?.labels?.selectClass || "Select Class"
                }
                required
                error={errors.classId}
                disabled={isEditMode}
              />
            </div>

            {/* Medium Dropdown - static options */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations?.labels?.medium || "Medium"}
                name="medium"
                value={formData.medium}
                onChange={(value) => handleDropdownChange("medium", value)}
                options={mediumOptions}
                placeholder={
                  translations?.labels?.selectMedium || "Select Medium"
                }
                required
                error={errors.medium}
                disabled={isEditMode}
              />
            </div>

            {/* Group Dropdown - shown only for XI / XII */}
            {isHigherClass && (
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label={translations?.labels?.groupName || "Group"}
                  name="groupId"
                  value={formData.groupId}
                  onChange={(value) => handleDropdownChange("groupId", value)}
                  options={groupOptions}
                  placeholder={
                    translations?.labels?.selectGroup || "Select Group"
                  }
                  required
                  error={errors.groupId}
                  disabled={isEditMode}
                />
              </div>
            )}

            {/* Subjects - shown only for XI / XII, read-only */}
            {isHigherClass && (
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={translations?.labels?.subjects || "Subjects"}
                  name="subjects"
                  type="text"
                  value={formData.subjects}
                  disabled
                  readOnly
                />
              </div>
            )}

            {/* Total Capacity - text type, numbers only, max 3 digits */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations?.labels?.totalCapacity || "Total Capacity"}
                name="totalCapacity"
                type="text"
                value={formData.totalCapacity}
                onChange={handleCapacityChange}
                placeholder={
                  translations?.labels?.totalCapacityPlaceholder ||
                  "Total Capacity Count"
                }
                required
                error={errors.totalCapacity}
                maxLength={3}
                disabled
                readOnly
              />
            </div>
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                isEditMode
                  ? translations?.labels?.updating || "Updating..."
                  : translations?.labels?.submitting || "Submitting..."
              }
            >
              {isEditMode
                ? translations?.labels?.updateButton || "UPDATE"
                : translations?.labels?.save || "SUBMIT"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations?.labels?.cancel || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdmissionSeatAllocationConfigCreate;
